import { action, atom, withInit } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { CurrencyModel } from '@root/entities/currency'
import { nativePriceAtom } from '@root/features/realtime/atoms'
import {
  matchTradePriceType,
  tradePriceType,
} from '@root/features/trade/trading/atoms'
import { MathUtil, NumberUtil } from '@root/shared'
import { NumericFormat } from '@root/shared/core/format'
import { reatomNumericInput } from '@root/shared/lib/reatom'

import type { TypeHelper } from '@blade/std-lib'
import type { Atom, Ctx, CtxSpy } from '@reatom/framework'
import type { LimitOrder } from '@root/entities/limit-order'
import type { LimitOrderSellModel } from '@root/entities/limit-order/atoms/reatom-limit-order-sell'
import type { PairModel } from '@root/entities/pair'
import type { WalletModel } from '@root/entities/wallet/atoms'
import type {
  InsufficientBalanceError,
  NoAmountError,
} from '@root/features/trade/validation-errors'

export type LimitSellValidationError = InsufficientBalanceError | NoAmountError

type Args = {
  pairAtom: Atom<PairModel | undefined>
  walletAtom: Atom<WalletModel | undefined>
  sellAmountCurrencyAtom: Atom<BigNumber>
  initialOrder?: LimitOrderSellModel | undefined
}

export const reatomLimitSellInput = ({
  walletAtom,
  pairAtom,
  initialOrder,
  sellAmountCurrencyAtom,
}: Args) => {
  const pairQuote = atom((ctx) => ctx.spy(pairAtom)?.quote, 'pairQuote')

  const targetTypeAtom = tradePriceType.pipe(
    withInit(() => {
      return initialOrder?.triggerType ?? 'absolute'
    }),
  )

  const sellLowPercentsAtom = reatomNumericInput(
    {
      initialAmount: -1,
    },
    'sellLowPercentsAtom',
  )
  const sellHighPercentsAtom = reatomNumericInput(
    {
      initialAmount: 1,
    },
    'sellHighPercentsAtom',
  )

  const calculateTargetUsd = (ctx: CtxSpy, percents: BigNumber) => {
    const targetType = ctx.spy(targetTypeAtom)

    const pair = ctx.spy(pairAtom)
    if (!pair) {
      return BigNumber(0)
    }

    if (targetType === 'absolute') {
      const currencyPriceUsd =
        initialOrder?.costAtCreationUsd ?? ctx.get(pair.price).priceUsd

      const sellHighUsd = currencyPriceUsd.plus(
        currencyPriceUsd.multipliedBy(percents.dividedBy(100)),
      )
      return sellHighUsd
    }

    const marketCapUsd =
      initialOrder?.marketCapAtCreationUsd ?? ctx.get(pair.marketCapUsd)
    const targetMarketCapUsd = marketCapUsd.plus(
      marketCapUsd.multipliedBy(percents.dividedBy(100)),
    )

    return targetMarketCapUsd
  }

  const setPriceUsdAction = action(
    (
      ctx: Ctx,
      args: {
        type: 'low' | 'high'
        priceUsd: BigNumber
      },
    ) => {
      const pair = ctx.get(pairAtom)
      if (!pair) {
        return
      }

      const targetType = ctx.get(targetTypeAtom)

      const targetPercentageAtom =
        args.type === 'low' ? sellLowPercentsAtom : sellHighPercentsAtom

      const targetBasePrice = matchTradePriceType(targetType, {
        onAbsolute: () =>
          initialOrder?.costAtCreationUsd ?? ctx.get(pair.price).priceUsd,
        'onMarket-cap': () =>
          initialOrder?.marketCapAtCreationUsd ?? ctx.get(pair.marketCapUsd),
      })

      const calculatedPercent = MathUtil.percentageChangeBn(
        targetBasePrice,
        args.priceUsd,
      )

      targetPercentageAtom.setRaw(
        ctx,
        NumericFormat.formatPreset(calculatedPercent, 'full'),
      )
    },
    'setPriceUsdAction',
  )

  const sellLowUsdAtom = atom((ctx) => {
    const sellLowPercents = ctx.spy(sellLowPercentsAtom)
    return calculateTargetUsd(ctx, sellLowPercents)
  }, 'sellLowUsdAtom')

  const sellHighUsdAtom = atom((ctx) => {
    const sellHighPercents = ctx.spy(sellHighPercentsAtom)
    return calculateTargetUsd(ctx, sellHighPercents)
  }, 'sellHighUsdAtom')

  const quoteReceiveThresholdAtom = atom<'low' | 'high'>(
    'high',
    'quoteReceiveThresholdAtom',
  )
  sellLowPercentsAtom.raw.onChange((ctx) => {
    quoteReceiveThresholdAtom(ctx, 'low')
  })
  sellHighPercentsAtom.raw.onChange((ctx) => {
    quoteReceiveThresholdAtom(ctx, 'high')
  })

  const resolveTargetPriceUsd = (ctx: CtxSpy, targetUsd: BigNumber) => {
    const currency = ctx.spy(pairQuote)
    const targetType = ctx.spy(targetTypeAtom)

    if (!currency) {
      return BigNumber(0)
    }

    if (targetType === 'market-cap') {
      return CurrencyModel.calculatePriceFromMarketCap(currency, targetUsd)
    }

    return targetUsd
  }

  const receiveAtom = atom((ctx) => {
    const threshold = ctx.spy(quoteReceiveThresholdAtom)
    const sellLowUsd = ctx.spy(sellLowUsdAtom)
    const sellHighUsd = ctx.spy(sellHighUsdAtom)
    const targetUsd = threshold === 'low' ? sellLowUsd : sellHighUsd
    const targetPriceUsd = resolveTargetPriceUsd(ctx, targetUsd)
    const sellAmount = ctx.spy(sellAmountCurrencyAtom)
    const totalUsdAmount = targetPriceUsd.multipliedBy(sellAmount)

    const nativePrice = ctx.spy(nativePriceAtom)
    return NumberUtil.safeBigNumber(totalUsdAmount.dividedBy(nativePrice), 0)
  }, 'receiveAtom')

  const outputSettingsAtom = atom(
    (
      ctx,
    ):
      | TypeHelper.OmitKey<
          LimitOrder.LimitOrderSellSettings,
          'expirationDate' | 'deadline'
        >
      | undefined => {
      const pair = ctx.get(pairAtom)
      const wallet = ctx.get(walletAtom)
      const sellAmount = ctx.get(sellAmountCurrencyAtom)

      if (!pair || !wallet || sellAmount.isZero()) {
        return undefined
      }

      const targetType = ctx.get(targetTypeAtom)

      const sellHigh = ctx.get(sellHighUsdAtom)

      const sellLow = ctx.get(sellLowUsdAtom)

      return {
        type: targetType,
        valueHigh: sellHigh,
        valueLow: sellLow,
        sellAmountCurrency: sellAmount,
      }
    },
    'outputSettingsAtom',
  )

  return {
    targetTypeAtom,
    sellLowPercentsAtom,
    sellLowUsdAtom,
    sellHighPercentsAtom,
    sellHighUsdAtom,
    receiveAtom,
    pairAtom,
    walletAtom,
    outputSettingsAtom,
    setPriceUsdAction,
  }
}
export type LimitSellInputReatom = ReturnType<typeof reatomLimitSellInput>
